import { useMemo } from "react";
import useSWR, { SWRResponse } from "swr";
import {
  CompilationStatus,
  type CompilationV1,
  type CompilationV2,
  type Compilation,
  CompilationV3,
} from "@memento/types/compilation";
import {
  isCompilationV1OrV2,
  isCompilationV3,
} from "@memento/library/compilation";
import { useAuthenticatedFetcher, getLoadingStatus } from "src/libs/fetcher";
import { getCardCompilationsEndpointUrl } from "src/libs/api-endpoint-lib";

export type UseCardCompilationsResult = {
  compilations: Compilation[];
  /** The latest compilation in the CREATED state. This is a CompilationV3
   * because only v3 compilations will be in this state and CREATED v3
   * compilations are what we use in the render UI. */
  latestCreatedCompilation: CompilationV3 | undefined;
  /** The latest compilation that *is not* in the CREATED state (any state
   * except CREATED) */
  latestStartedCompilation: Compilation | undefined;
  /** The latest compilation that is in the COMPLETE state. This could be the
   * same as latestStartedCompilation. */
  latestCompleteCompilation: Compilation | undefined;
  /** The latest compilation in any state. */
  latestCompilation: Compilation | undefined;
  latestV2Compilation: CompilationV1 | CompilationV2 | undefined;
  reloadCompilation: () => Promise<void>;
};

export const useCardCompilations = (
  cardId: string | undefined | null,
  cardUrl: string | undefined | null
) => {
  const fetcher = useAuthenticatedFetcher();
  const key =
    cardId && cardUrl ? getCardCompilationsEndpointUrl(cardUrl, cardId) : null;

  const { data, error, mutate }: SWRResponse<Compilation[], Error> = useSWR(
    key,
    (url: string) => fetcher(url)
  );

  const result = useMemo<UseCardCompilationsResult | null>(() => {
    if (!data) {
      return null;
    }

    // Sort the compilations from oldest (first) to newest (last)
    const compilations = data.sort((a, b) => a.createdAt - b.createdAt);

    const latestCreatedCompilation = compilations.findLast(
      (compilation) =>
        compilation.compilationStatus === CompilationStatus.CREATED
    );

    const latestStartedCompilation = compilations.findLast(
      (compilation) =>
        compilation.compilationStatus !== CompilationStatus.CREATED
    );

    const latestCompleteCompilation = compilations.findLast(
      (compilation) =>
        compilation.compilationStatus === CompilationStatus.COMPLETE
    );

    const latestCompilation = compilations[compilations.length - 1];

    const compilationsV2 = compilations.filter(isCompilationV1OrV2);

    const latestV2Compilation = compilationsV2[compilationsV2.length - 1];

    const reloadCompilation = async () => {
      await mutate();
    };

    return {
      compilations,
      latestCompleteCompilation,
      latestCreatedCompilation:
        latestCreatedCompilation && isCompilationV3(latestCreatedCompilation)
          ? latestCreatedCompilation
          : undefined,
      latestStartedCompilation,
      latestCompilation,
      latestV2Compilation,
      reloadCompilation,
    };
  }, [data, mutate]);

  return {
    result,
    error,
    status: getLoadingStatus(key, data, error),
    mutate,
  };
};
